import React, { useEffect, useState } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import { PAGINATION_SETTINGS } from '../constants';
import { getTasks, getAssignedTasks } from '../service/weexpertService';
import { useTranslation } from "react-i18next";
import { DataTablePagination } from "@/components/ui/table-pagination";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import { Input } from '@/components/ui/input.jsx';
import { Button } from '@/components/ui/button.jsx';
import { flexRender, getCoreRowModel, getPaginationRowModel, useReactTable } from "@tanstack/react-table";
import { useQuery, useQueryClient } from "react-query";
import { Skeleton } from "@mui/material";
import { Badge } from "@/components/ui/badge";
import { format } from "date-fns";
import { formatInWarsaw, isExpiredInWarsaw } from '../utils/timezone';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Search } from "@/components/ui/combobox-search";
import { getCurrentUser, getClients } from "../service/weexpertService";
import webSocketService from "../services/websocket";

const getStatusColor = (status) => {
  switch (status) {
    case 'PENDING':
      return 'bg-yellow-100 text-yellow-800';
    case 'IN_PROGRESS':
      return 'bg-blue-100 text-blue-800';
    case 'COMPLETED':
      return 'bg-green-100 text-green-800';
    case 'OVERDUE':
      return 'bg-red-100 text-red-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

const columns = (t) => {
  return [
    {
      accessorKey: "title",
      header: t('task_title'),
      cell: ({ row }) => {
        return (
          <Link to={`/admin/task/details/${row.original.id}`}>
            {row.original.title}
          </Link>
        )
      },
      key: uuidv4()
    },
    {
      accessorKey: "status",
      header: t('status'),
      cell: ({ row }) => (
        <Badge className={getStatusColor(row.original.status)}>
          {row.original.status}
        </Badge>
      ),
      key: uuidv4()
    },
    {
      accessorKey: "assignedTo",
      header: t('assigned_to'),
      cell: ({ row }) => (
        <span>
          {row.original.assignedTo?.firstName} {row.original.assignedTo?.lastName}
        </span>
      ),
      key: uuidv4()
    },
    {
      accessorKey: "assignedBy",
      header: t('assigned_by'),
      cell: ({ row }) => (
        <span>
          {row.original.assignedBy?.firstName} {row.original.assignedBy?.lastName}
        </span>
      ),
      key: uuidv4()
    },
    {
      accessorKey: "deadline",
      header: t('deadline'),
      cell: ({ row }) => (
        <span className={isExpiredInWarsaw(row.original.deadline) && row.original.status !== 'COMPLETED' ? 'text-red-600' : ''}>
          {formatInWarsaw(row.original.deadline, 'dd/MM/yyyy HH:mm')}
        </span>
      ),
      key: uuidv4()
    },
    {
      accessorKey: "client",
      header: t('client'),
      cell: ({ row }) => (
        <span>
          {row.original.client ? `${row.original.client.firstName} ${row.original.client.lastName}` : ''}
        </span>
      ),
      key: uuidv4()
    },
    {
      accessorKey: "createdAt",
      header: t('created'),
      cell: ({ row }) => (
        <span>
          {formatInWarsaw(row.original.createdAt, 'dd/MM/yyyy')}
        </span>
      ),
      key: uuidv4()
    }
  ]
}

const TasksPage = () => {
  const { t } = useTranslation('tasks');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGINATION_SETTINGS.users || 10);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: rowsPerPage });
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [taskTypeFilter, setTaskTypeFilter] = useState('ALL');
  const [clientFilter, setClientFilter] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'assigned'

  const navigate = useNavigate()
  const { data: user } = useQuery('currentUser', getCurrentUser);
  const isAdmin = user?.data?.roles === 'ADMIN' || user?.data?.roles === 'SUPER_ADMIN';
  const queryClient = useQueryClient();

  const filters = {
    status: statusFilter === 'ALL' ? '' : statusFilter,
    taskType: taskTypeFilter === 'ALL' ? '' : taskTypeFilter,
    clientId: clientFilter ? clientFilter.value : '',
  };

  const { data: tasksData, isLoading } = useQuery({
    queryKey: ['tasks', page, rowsPerPage, statusFilter, taskTypeFilter, clientFilter, viewMode],
    queryFn: () => {
      if (viewMode === 'assigned') {
        return getAssignedTasks(filters);
      } else {
        return getTasks(filters);
      }
    }
  });

  useEffect(() => {
    setPage(pagination.pageIndex)
    setRowsPerPage(pagination.pageSize)
  }, [pagination])

  // WebSocket event listeners for real-time updates
  useEffect(() => {
    // Connect to WebSocket service
    webSocketService.connect();

    // Handle task notifications
    const handleTaskNotification = (event) => {
      console.log('Task notification received in tasks page:', event.detail);
      // Invalidate tasks queries to refresh the list
      queryClient.invalidateQueries(['tasks']);
      queryClient.invalidateQueries(['taskNotifications']);
    };

    // Handle task updates
    const handleTaskUpdate = (event) => {
      console.log('Task updated in tasks page:', event.detail);
      // Invalidate tasks queries to refresh the list
      queryClient.invalidateQueries(['tasks']);
    };

    // Handle task status updates
    const handleTaskStatusUpdate = (event) => {
      console.log('Task status updated in tasks page:', event.detail);
      // Invalidate tasks queries to refresh the list
      queryClient.invalidateQueries(['tasks']);
    };

    // Add event listeners
    window.addEventListener('task:notification', handleTaskNotification);
    window.addEventListener('task:updated', handleTaskUpdate);
    window.addEventListener('task:status_updated', handleTaskStatusUpdate);

    // Cleanup function
    return () => {
      window.removeEventListener('task:notification', handleTaskNotification);
      window.removeEventListener('task:updated', handleTaskUpdate);
      window.removeEventListener('task:status_updated', handleTaskStatusUpdate);
    };
  }, [queryClient]);

  const tasks = tasksData?.data || [];

  const table = useReactTable({
    data: isLoading ? Array(rowsPerPage).fill({}) : tasks,
    columns: isLoading ? columns(t).map((column) => {
      return {
        ...column,
        cell: () => {
          return <Skeleton />
        }
      }
    }) : columns(t),
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    onPaginationChange: setPagination,
    manualPagination: true,
    rowCount: tasks?.length ?? 0,
    state: {
      pagination
    },
    initialState: {
      pagination
    }
  });

  if (user && user.data && user.data.roles === 'CLIENT') {
    navigate('/404')
  }
  return (
    <>
      <div className="relative">
        <div className="sticky left-0 top-16 z-10 w-full bg-grayLightMainBg">
          <div className="flex justify-between py-8 pr-8 items-center bg-grayLightMainBg">
            <div className="ml-8 text-4xl ">
              {t("title")}
            </div>
            <div className="flex w-5/6 items-center space-x-2">
              {isAdmin && (
                <Select value={viewMode} onValueChange={setViewMode}>
                  <SelectTrigger className="w-[150px] bg-white h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("all_tasks")}</SelectItem>
                    <SelectItem value="assigned">{t("my_tasks")}</SelectItem>
                  </SelectContent>
                </Select>
              )}

              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[150px] bg-white h-12">
                  <SelectValue placeholder={t("filter_by_status")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t("all_status")}</SelectItem>
                  <SelectItem value="PENDING">{t("pending")}</SelectItem>
                  <SelectItem value="IN_PROGRESS">{t("in_progress")}</SelectItem>
                  <SelectItem value="COMPLETED">{t("completed")}</SelectItem>
                  <SelectItem value="OVERDUE">{t("overdue")}</SelectItem>
                </SelectContent>
              </Select>

              <Select value={taskTypeFilter} onValueChange={setTaskTypeFilter}>
                <SelectTrigger className="w-[150px] bg-white h-12">
                  <SelectValue placeholder={t("filter_by_type")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">{t("all_types")}</SelectItem>
                  <SelectItem value="TEXT">{t("text")}</SelectItem>
                  <SelectItem value="FORM">{t("form")}</SelectItem>
                </SelectContent>
              </Select>

              <Search
                fetchOption={(q) => {
                  return getClients(100, 0, q)
                    .then((res) => {
                      return res.data.clients
                        .map(client => {
                          return {
                            label: `${client.firstName} ${client.lastName}`,
                            value: client.id,
                            ...client
                          }
                        })
                    })
                }}
                placeholder={t("filter_by_client")}
                value={clientFilter?.label || null}
                onChange={setClientFilter}
                className="w-[200px] !pb-1"
              />

              {isAdmin && (
                <Link to='/admin/create/task'>
                  <Button className=" mr-2 text-blueText bg-blueLightAccent hover:bg-blueLightAccent border-[1px] border-blueBorder shadow-none">
                    {t("create_task")}
                  </Button>
                </Link>
              )}
            </div>
          </div>
        </div>
        <div className="w-full px-8 py-4 bg-grayLightMainBg">
          <div className="rounded-md border p-2">
            <Table>
              <TableHeader>
                {table.getHeaderGroups().map(headerGroup => (
                  <TableRow key={uuidv4()}>
                    {headerGroup.headers.map(header => {
                      return (
                        <TableHead className="pt-3 pb-3"
                          key={uuidv4()}>
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext()
                            )}
                        </TableHead>
                      )
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map(row => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && "selected"}
                      className="hover:bg-gray-50"
                    >
                      {row.getVisibleCells().map(cell => (
                        <TableCell key={cell.id}>
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={columns(t).length} className="h-24 text-center">
                      {isLoading ? t("loading") : t("no_tasks_found")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
          <DataTablePagination table={table} />
        </div>
      </div>
    </>
  );
};

export default TasksPage;
